// src/components/admin/ContasCupons/CouponsImpact.tsx
// Impacto de Promoções e Cupons - IDENTIDADE GRAIA

import React, { useState } from 'react';
import {
    Ticket,
    AlertCircle,
    DollarSign,
    Package,
    Lightbulb,
    ShoppingCart,
    BarChart3,
    Award
} from 'lucide-react';
import './CouponsImpact.css';
import { useCouponsImpact } from '../../../hooks/useAdminQueries';

interface CouponData {
    code: string;
    salesCount: number;
    totalRevenueCents: number;
    totalDiscountCents: number;
    averageTicketCents: number;
    averageDiscountCents: number;
    products: string[];
}

interface CouponsImpactProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const CouponsImpact: React.FC<CouponsImpactProps> = ({ onShowToast: _onShowToast }) => {

    // ── 1. useState PRIMEIRO ──────────────────────────────
    const [period, setPeriod] = useState('30');

    // ── 2. React Query hooks DEPOIS ───────────────────────
    const { data, isLoading: loading } = useCouponsImpact(period);

    const formatCurrency = (cents: number) => `R$ ${(cents / 100).toFixed(2)}`;

    if (loading) {
        return (
            <div className="cpn-imp-loading">
                <div className="spinner"></div>
                <p>Carregando análise...</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="cpn-imp-error">
                <AlertCircle size={48} />
                <p>Erro ao carregar dados</p>
            </div>
        );
    }

    return (
        <div className="cpn-imp-container">
            {/* Controles */}
            <div className="cpn-imp-controls">
                <div className="cpn-imp-period-selector">
                    <span>Período:</span>
                    {['7', '30', '90', '365', 'all'].map((p) => (
                        <button key={p} className={period === p ? 'active' : ''} onClick={() => setPeriod(p)}>
                            {p === 'all' ? 'Tudo' : `${p}d`}
                        </button>
                    ))}
                </div>
            </div>

            {/* Insight Automático */}
            {data.insight && (
                <div className="cpn-imp-insight">
                    <Lightbulb size={20} />
                    <p>{data.insight}</p>
                </div>
            )}

            {/* Resumo Geral */}
            <section className="cpn-imp-section">
                <h3><BarChart3 size={20} /> Resumo de Uso de Cupons</h3>
                <div className="cpn-imp-summary-grid">
                    <div className="cpn-imp-summary-card">
                        <div className="cpn-imp-summary-icon" style={{ background: '#3b82f6' }}>
                            <ShoppingCart size={24} />
                        </div>
                        <div className="cpn-imp-summary-content">
                            <span className="cpn-imp-summary-label">Total de Vendas</span>
                            <span className="cpn-imp-summary-value">{data.summary.totalSales}</span>
                        </div>
                    </div>

                    <div className="cpn-imp-summary-card">
                        <div className="cpn-imp-summary-icon" style={{ background: '#10b981' }}>
                            <Ticket size={24} />
                        </div>
                        <div className="cpn-imp-summary-content">
                            <span className="cpn-imp-summary-label">Vendas com Cupom</span>
                            <span className="cpn-imp-summary-value">{data.summary.salesWithCoupon}</span>
                            <span className="cpn-imp-summary-detail">{data.summary.couponUsagePercentage}% do total</span>
                        </div>
                    </div>

                    <div className="cpn-imp-summary-card">
                        <div className="cpn-imp-summary-icon" style={{ background: '#6b7280' }}>
                            <Package size={24} />
                        </div>
                        <div className="cpn-imp-summary-content">
                            <span className="cpn-imp-summary-label">Vendas sem Cupom</span>
                            <span className="cpn-imp-summary-value">{data.summary.salesWithoutCoupon}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Impacto Financeiro */}
            <section className="cpn-imp-section">
                <h3><DollarSign size={20} /> Impacto Financeiro</h3>
                <div className="cpn-imp-financial-grid">
                    <div className="cpn-imp-financial-card">
                        <h4>Com Cupom</h4>
                        <div className="cpn-imp-financial-amount success">
                            {formatCurrency(data.financial.revenueWithCouponCents)}
                        </div>
                        <div className="cpn-imp-financial-details">
                            <div className="cpn-imp-detail-row">
                                <span>Ticket Médio:</span>
                                <span>{formatCurrency(data.averageTickets.withCouponCents)}</span>
                            </div>
                            <div className="cpn-imp-detail-row">
                                <span>Total Descontado:</span>
                                <span className="negative">-{formatCurrency(data.financial.totalDiscountCents)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="cpn-imp-financial-card">
                        <h4>Sem Cupom</h4>
                        <div className="cpn-imp-financial-amount">
                            {formatCurrency(data.financial.revenueWithoutCouponCents)}
                        </div>
                        <div className="cpn-imp-financial-details">
                            <div className="cpn-imp-detail-row">
                                <span>Ticket Médio:</span>
                                <span>{formatCurrency(data.averageTickets.withoutCouponCents)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="cpn-imp-financial-card highlight">
                        <h4>Receita Incremental</h4>
                        <div className="cpn-imp-financial-amount">
                            {formatCurrency(data.financial.netIncrementalRevenueCents)}
                        </div>
                        <div className="cpn-imp-financial-details">
                            <div className="cpn-imp-detail-row">
                                <span>Desconto Sacrificado:</span>
                                <span className="negative">-{formatCurrency(data.financial.commissionSacrificedCents)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Top Cupons */}
            <section className="cpn-imp-section">
                <h3><Award size={20} /> Top 10 Cupons Mais Efetivos</h3>
                {data.topCoupons.length === 0 ? (
                    <p className="cpn-imp-no-data">Nenhum cupom utilizado neste período</p>
                ) : (
                    <div className="cpn-imp-table-container">
                        <table className="cpn-imp-table">
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Vendas</th>
                                    <th>Receita Gerada</th>
                                    <th>Desconto Total</th>
                                    <th>Ticket Médio</th>
                                    <th>Produtos</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.topCoupons.map((coupon: CouponData) => (
                                    <tr key={coupon.code}>
                                        <td><span className="cpn-imp-coupon-code">{coupon.code}</span></td>
                                        <td className="cpn-imp-sales">{coupon.salesCount}</td>
                                        <td className="cpn-imp-revenue">{formatCurrency(coupon.totalRevenueCents)}</td>
                                        <td className="cpn-imp-discount">-{formatCurrency(coupon.totalDiscountCents)}</td>
                                        <td>{formatCurrency(coupon.averageTicketCents)}</td>
                                        <td>
                                            <span className="cpn-imp-products-count">
                                                {coupon.products.length} produto{coupon.products.length !== 1 ? 's' : ''}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Cupons Problemáticos */}
            {data.problematicCoupons.length > 0 && (
                <section className="cpn-imp-section problematic">
                    <h3><AlertCircle size={20} /> Cupons Problemáticos (Desconto &gt; 50%)</h3>
                    <div className="cpn-imp-problematic-grid">
                        {data.problematicCoupons.map((coupon: CouponData) => {
                            const discountPercentage = ((coupon.totalDiscountCents / coupon.totalRevenueCents) * 100).toFixed(1);
                            return (
                                <div key={coupon.code} className="cpn-imp-problematic-card">
                                    <div className="cpn-imp-problematic-header">
                                        <span className="cpn-imp-code-large">{coupon.code}</span>
                                        <span className="cpn-imp-warning-badge">Alto Desconto</span>
                                    </div>
                                    <div className="cpn-imp-problematic-stats">
                                        <div className="cpn-imp-stat">
                                            <span className="cpn-imp-stat-label">Desconto Médio:</span>
                                            <span className="cpn-imp-stat-value negative">{discountPercentage}%</span>
                                        </div>
                                        <div className="cpn-imp-stat">
                                            <span className="cpn-imp-stat-label">Vendas:</span>
                                            <span className="cpn-imp-stat-value">{coupon.salesCount}</span>
                                        </div>
                                        <div className="cpn-imp-stat">
                                            <span className="cpn-imp-stat-label">Receita:</span>
                                            <span className="cpn-imp-stat-value">{formatCurrency(coupon.totalRevenueCents)}</span>
                                        </div>
                                    </div>
                                    <p className="cpn-imp-suggestion">Considere reduzir o desconto ou desativar este cupom</p>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Produtos Mais Vendidos com Cupom */}
            {data.topProductsWithCoupon.length > 0 && (
                <section className="cpn-imp-section">
                    <h3><Package size={20} /> Top Produtos Vendidos com Cupom</h3>
                    <div className="cpn-imp-products-grid">
                        {data.topProductsWithCoupon.map((product: { title: string; salesCount: number; revenueCents: number }, index: number) => (
                            <div key={index} className="cpn-imp-product-card">
                                <div className="cpn-imp-product-rank">{index + 1}º</div>
                                <div className="cpn-imp-product-info">
                                    <span className="cpn-imp-product-title">{product.title}</span>
                                    <div className="cpn-imp-product-stats">
                                        <span>{product.salesCount} vendas</span>
                                        <span className="cpn-imp-separator">•</span>
                                        <span>{formatCurrency(product.revenueCents)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};

export default CouponsImpact;